import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Clock, TrendUp, FileText, Calendar, User, ChartLine, Upload } from '@phosphor-icons/react';
import api from '../utils/api';
import { toast } from 'sonner';
import BMIDisplay from './BMIDisplay';
import { VoiceInput } from './VoiceInput';

const THERAPY_OPTIONS = [
  'mat',
  'jade water',
  'chm 123',
  'chm 1',
  'mother chair',
  'vibration chair',
  'baby chair',
  'san veg',
  '3ball',
  '9ball',
  'LF pad',
  'Leg Msg',
  'Photon Belt',
  'Steamer',
  'EMS vibration'
];

const CATEGORY_COLORS = {
  'New Customer': 'bg-blue-100 text-blue-800 border-blue-300',
  'New': 'bg-green-100 text-green-800 border-green-300',
  '10 Days': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  '30 Days': 'bg-orange-100 text-orange-800 border-orange-300',
  '60 Days': 'bg-red-100 text-red-800 border-red-300',
  '90 Days': 'bg-purple-100 text-purple-800 border-purple-300',
  '120 Days': 'bg-pink-100 text-pink-800 border-pink-300',
  'Buyers': 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Challenge Therapy': 'bg-indigo-100 text-indigo-800 border-indigo-300'
};

const ClientTrackingModal = ({ open, onClose, customer, batchTime }) => {
  const [loading, setLoading] = useState(false);
  const [sessionNotes, setSessionNotes] = useState([]);
  const [categoryTimeline, setCategoryTimeline] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    session_notes: '',
    treatment_effectiveness: 50,
    improvement_action_notes: '',
    improvement_result_notes: '',
    therapy_list: [],
    report_image: null,
    recommended_next_steps: ''
  });
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (open && customer) {
      fetchData();
      // Reset form
      setFormData({
        session_notes: '',
        treatment_effectiveness: 50,
        improvement_action_notes: '',
        improvement_result_notes: '',
        therapy_list: [],
        report_image: null,
        recommended_next_steps: ''
      });
      setImagePreview(null);
    }
  }, [open, customer]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch session notes
      const notesResponse = await api.get(`/customers/${customer.customer_id}/session-notes`);
      setSessionNotes(notesResponse.data.notes || []);
      
      // Fetch category timeline
      const timelineResponse = await api.get(`/customers/${customer.customer_id}/category-timeline`);
      setCategoryTimeline(timelineResponse.data);
      
    } catch (error) {
      toast.error('Failed to load tracking data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (loading) {
      return;
    }
    
    // Validate required fields
    if (!formData.session_notes.trim()) {
      toast.error('Please enter session notes');
      return;
    }
    
    try {
      setLoading(true);
      
      await api.post(`/customers/${customer.customer_id}/session-notes`, formData);
      
      toast.success('Session note saved successfully');
      
      // Reset form
      setFormData({
        session_notes: '',
        treatment_effectiveness: 50,
        improvement_action_notes: '',
        improvement_result_notes: '',
        therapy_list: [],
        report_image: null,
        recommended_next_steps: ''
      });
      setImagePreview(null);
      
      setShowAddForm(false);
      fetchData(); // Refresh data
      
    } catch (error) {
      toast.error('Failed to save session note');
    } finally {
      setLoading(false);
    }
  };

  const handleTherapyChange = (therapy, checked) => {
    setFormData(prev => ({
      ...prev,
      therapy_list: checked
        ? [...prev.therapy_list, therapy]
        : prev.therapy_list.filter(t => t !== therapy)
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image size should be less than 10MB');
        return;
      }
      
      // Upload to storage
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await api.post('/storage/upload/report', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        const storagePath = response.data.storage_path;
        setFormData(prev => ({
          ...prev,
          report_image: storagePath
        }));
        setImagePreview(`${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${storagePath}`);
        toast.success('Report image uploaded successfully!');
      } catch (error) {
        console.error('Report image upload failed:', error);
        toast.error('Failed to upload report image. Please try again.');
      }
    }
  };

  if (!customer) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl font-semibold text-[#2C4C3B] flex items-start gap-2 sm:gap-3 break-words">
            <User size={24} weight="fill" className="shrink-0 mt-1" />
            <span className="break-words">Client Tracking - {customer.name}</span>
          </DialogTitle>
          <div className="flex flex-wrap gap-2 mt-2">
            <Badge className="bg-[#2C4C3B] text-white text-xs sm:text-sm">Reg: {customer.reg_no}</Badge>
            {batchTime && <Badge variant="outline" className="text-xs sm:text-sm">Batch: {batchTime}</Badge>}
          </div>
        </DialogHeader>

        {loading && !sessionNotes.length ? (
          <div className="text-center py-8">Loading tracking data...</div>
        ) : (
          <div className="space-y-6">
            {/* Category Timeline */}
            {categoryTimeline && (
              <Card className="p-4 bg-gradient-to-r from-green-50 to-blue-50">
                <h3 className="font-semibold text-[#2C4C3B] mb-3 flex items-center gap-2">
                  <TrendUp size={20} />
                  Category Progression Timeline
                </h3>
                
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm text-[#1C1C1A]/60">Current:</span>
                  <Badge className={CATEGORY_COLORS[categoryTimeline.current_category] || 'bg-gray-100'}>
                    {categoryTimeline.current_category}
                  </Badge>
                  <span className="text-sm text-[#1C1C1A]/60">
                    • {categoryTimeline.total_sessions} total sessions
                  </span>
                </div>

                {/* Visual Timeline */}
                <div className="flex flex-wrap gap-2">
                  {categoryTimeline.timeline.map((entry, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Card className={`p-2 border-2 ${CATEGORY_COLORS[entry.category] || 'bg-gray-50'}`}>
                        <div className="text-xs font-medium">{entry.category}</div>
                        <div className="text-[10px] text-[#1C1C1A]/60">
                          {entry.session_count} sessions
                        </div>
                        <div className="text-[10px] text-[#1C1C1A]/50">
                          from {entry.start_date}
                        </div>
                      </Card>
                      {idx < categoryTimeline.timeline.length - 1 && (
                        <span className="text-[#1C1C1A]/40">→</span>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Add New Note Button */}
            {!showAddForm && (
              <Button
                onClick={() => setShowAddForm(true)}
                className="w-full bg-[#2C4C3B] hover:bg-[#1C3C2B] text-sm sm:text-base py-4 sm:py-2"
              >
                <FileText size={20} className="mr-2 shrink-0" />
                <span className="whitespace-normal sm:whitespace-nowrap">Add Today&apos;s Session Note</span>
              </Button>
            )}

            {/* Add Note Form */}
            {showAddForm && (
              <Card className="p-6 border-2 border-green-200">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <h3 className="font-semibold text-[#2C4C3B] mb-4">New Session Note</h3>

                  {/* Session Notes */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label>Session Notes *</Label>
                      <VoiceInput onText={(t) => setFormData((prev) => ({ ...prev, session_notes: (prev.session_notes ? prev.session_notes + ' ' : '') + t }))} />
                    </div>
                    <Textarea
                      data-testid="session-notes-textarea"
                      required
                      value={formData.session_notes}
                      onChange={(e) => setFormData({ ...formData, session_notes: e.target.value })}
                      placeholder="What did you discuss today? Any observations?"
                      className="min-h-[100px]"
                    />
                  </div>

                  {/* Treatment Effectiveness */}
                  <div>
                    <Label>Treatment Effectiveness: {formData.treatment_effectiveness}%</Label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.treatment_effectiveness}
                      onChange={(e) => setFormData({ ...formData, treatment_effectiveness: parseInt(e.target.value) })}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-[#1C1C1A]/60">
                      <span>0%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </div>

                  {/* Improvement Action Notes */}
                  <div>
                  <div className="flex items-center justify-between mb-1">
                      <Label>Improvement Action Notes</Label>
                      <VoiceInput onText={(t) => setFormData((prev) => ({ ...prev, improvement_action_notes: (prev.improvement_action_notes ? prev.improvement_action_notes + ' ' : '') + t }))} />
                    </div>
                    <Textarea
                      data-testid="improvement-notes-textarea"
                      value={formData.improvement_action_notes}
                      onChange={(e) => setFormData({ ...formData, improvement_action_notes: e.target.value })}
                      placeholder="What actions were taken to improve the condition?"
                      rows={3}
                    />
                  </div>

                  {/* Improvement Result % wise Notes */}
                  <div>
                    <Label>Improvement Result % wise Notes</Label>
                    <Textarea
                      value={formData.improvement_result_notes}
                      onChange={(e) => setFormData({ ...formData, improvement_result_notes: e.target.value })}
                      placeholder="Describe the percentage-wise improvement observed (e.g., 70% improvement in mobility)"
                      rows={3}
                    />
                  </div>

                  {/* What Therapy Today */}
                  <div>
                    <Label className="mb-2 sm:mb-3 block text-sm sm:text-base">What Therapy Today</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 max-h-48 overflow-y-auto border rounded-lg p-2 sm:p-3">
                      {THERAPY_OPTIONS.map((therapy) => (
                        <div key={therapy} className="flex items-center gap-2">
                          <Checkbox
                            id={therapy}
                            checked={formData.therapy_list.includes(therapy)}
                            onCheckedChange={(checked) => handleTherapyChange(therapy, checked)}
                          />
                          <label htmlFor={therapy} className="text-xs sm:text-sm cursor-pointer">
                            {therapy}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Upload Report Image */}
                  <div>
                    <Label>Upload Report Image</Label>
                    <div className="mt-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="report-image-upload"
                      />
                      <label
                        htmlFor="report-image-upload"
                        className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#2C4C3B] hover:bg-green-50 transition-all"
                      >
                        <Upload size={20} />
                        <span className="text-sm">
                          {imagePreview ? 'Change Image' : 'Click to upload image (Max 5MB)'}
                        </span>
                      </label>
                      
                      {imagePreview && (
                        <div className="mt-3 relative">
                          <img
                            src={imagePreview}
                            alt="Report preview"
                            className="max-h-40 rounded-lg border"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setFormData({ ...formData, report_image: null });
                              setImagePreview(null);
                            }}
                            className="mt-2"
                          >
                            Remove Image
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Recommended Next Steps */}
                  <div>
                    <Label>Recommended Next Steps</Label>
                    <Textarea
                      value={formData.recommended_next_steps}
                      onChange={(e) => setFormData({ ...formData, recommended_next_steps: e.target.value })}
                      placeholder="What should be the focus for the next session?"
                      rows={3}
                    />
                  </div>

                  {/* Form Actions */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAddForm(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-[#2C4C3B]"
                    >
                      {loading ? 'Saving...' : 'Save Session Note'}
                    </Button>
                  </div>
                </form>
              </Card>
            )}

            {/* Previous Session Notes */}
            <div>
              <h3 className="font-semibold text-[#2C4C3B] mb-3 flex items-center gap-2">
                <ChartLine size={20} />
                Previous Session Notes ({sessionNotes.length})
              </h3>

              {sessionNotes.length === 0 ? (
                <Card className="p-6 text-center text-[#1C1C1A]/60">
                  No session notes yet. Add the first one above!
                </Card>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {sessionNotes.map((note) => (
                    <Card key={note.note_id} className="p-4 border-l-4 border-[#2C4C3B]">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <Calendar size={16} className="text-[#2C4C3B]" />
                          <span className="font-medium">{note.session_date}</span>
                          {note.batch_time && (
                            <Badge variant="outline" className="text-xs">
                              {note.batch_time}
                            </Badge>
                          )}
                        </div>
                        <Badge className="bg-green-100 text-green-800">
                          {note.treatment_effectiveness}% effective
                        </Badge>
                      </div>

                      <p className="text-sm text-[#1C1C1A] mb-3">{note.session_notes}</p>

                      {/* BMI Data Display */}
                      {note.bmi_data && (
                        <BMIDisplay bmiData={note.bmi_data} />
                      )}

                      {/* Improvement Notes */}
                      {note.improvement_action_notes && (
                        <div className="mb-3 p-2 bg-blue-50 rounded text-xs">
                          <span className="font-medium text-blue-800">Action: </span>
                          <span className="text-[#1C1C1A]/80">{note.improvement_action_notes}</span>
                        </div>
                      )}

                      {note.improvement_result_notes && (
                        <div className="mb-3 p-2 bg-green-50 rounded text-xs">
                          <span className="font-medium text-green-800">Result: </span>
                          <span className="text-[#1C1C1A]/80">{note.improvement_result_notes}</span>
                        </div>
                      )}

                      {/* Therapy List */}
                      {note.therapy_list && note.therapy_list.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-[#1C1C1A]/60 mb-1">Therapies Used:</p>
                          <div className="flex flex-wrap gap-1">
                            {note.therapy_list.map((therapy) => (
                              <Badge key={therapy} variant="outline" className="text-xs bg-purple-50">
                                {therapy}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Report Image */}
                      {note.report_image && (
                        <div className="mb-3">
                          <p className="text-xs text-[#1C1C1A]/60 mb-2">Report Image:</p>
                          <img
                            src={note.report_image.startsWith('data:') ? note.report_image : `${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${note.report_image}`}
                            alt="Session report"
                            className="max-h-32 rounded border cursor-pointer hover:opacity-80"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Create a new window with the image
                              const win = window.open();
                              const imageUrl = note.report_image.startsWith('data:') ? note.report_image : `${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${note.report_image}`;
                              win.document.write(`
                                <html>
                                  <head>
                                    <title>Session Report Image</title>
                                    <style>
                                      body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #000; }
                                      img { max-width: 100%; max-height: 100vh; object-fit: contain; }
                                    </style>
                                  </head>
                                  <body>
                                    <img src="${imageUrl}" alt="Report Image" />
                                  </body>
                                </html>
                              `);
                              win.document.close();
                            }}
                          />
                        </div>
                      )}

                      {note.recommended_next_steps && (
                        <div className="p-2 bg-blue-50 rounded text-xs">
                          <span className="font-medium text-blue-800">Next Steps: </span>
                          <span className="text-[#1C1C1A]/80">{note.recommended_next_steps}</span>
                        </div>
                      )}

                      <div className="text-xs text-[#1C1C1A]/50 mt-3 pt-2 border-t">
                        Added by {note.created_by_name} • {new Date(note.created_at).toLocaleString()}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClientTrackingModal;
